import { inject, Injectable } from '@angular/core';
import { Apollo, QueryRef } from 'apollo-angular';
import { filter, map } from 'rxjs/operators';
import { SignatureType, TenantInformation } from '@ui/shared/model';

import {
  currentStatusQuery,
  CurrentStatusQueryResponse,
  signingUrlQuery,
  SigningUrlQueryResponse
} from '@ui/legacy-lib';
import {
  digitalContractQuery,
  DigitalContractQueryResult,
  QueryBaseResponse,
  sendMessageQuery,
  sendTenantInfoMutation,
  SendTenantInfoResponse
} from '../queries/digital-contract.queries';
import { notificationConfig } from '../../config/notification.config';

@Injectable({
  providedIn: 'root'
})
export class DigitalContractFacade {
  private apollo = inject(Apollo);

  private aesStatusQueryRef: QueryRef<CurrentStatusQueryResponse>;

  public loadDigitalContract(token: string) {
    return this.apollo
      .query<DigitalContractQueryResult>({
        query: digitalContractQuery,
        variables: { token }
      })
      .pipe(
        map(response => {
          const contract = response?.data?.digitalContract;

          if (!contract) {
            throw new Error(notificationConfig.property.fetch.error);
          }

          return contract;
        })
      );
  }

  public sendMessage(token: string) {
    return this.apollo.query<QueryBaseResponse>({
      variables: { token },
      query: sendMessageQuery
    });
  }

  public sendTenantInfo(info: TenantInformation, signatureType: SignatureType) {
    return this.apollo
      .mutate<SendTenantInfoResponse>({
        mutation: sendTenantInfoMutation,
        variables: { info },
        update:
          signatureType === SignatureType.AES_MAIL
            ? () => this.refetchAesStatus()
            : null
      })
      .pipe(map(response => response.data.sendTenantInfo));
  }

  public currentStatus(token: string) {
    this.aesStatusQueryRef = this.apollo.watchQuery<CurrentStatusQueryResponse>(
      {
        variables: { token },
        query: currentStatusQuery,
        fetchPolicy: 'no-cache'
      }
    );
    return this.aesStatusQueryRef.valueChanges.pipe(
      filter(response => response.dataState === 'complete'),
      map(response => response.data.currentStatus)
    );
  }

  public getSigningUrl(token: string, redirectUrl) {
    return this.apollo
      .query<SigningUrlQueryResponse>({
        variables: { token, redirectUrl },
        query: signingUrlQuery,
        fetchPolicy: 'no-cache'
      })
      .pipe(map(response => response.data.getSigningUrl));
  }

  private refetchAesStatus(): void {
    if (this.aesStatusQueryRef) {
      void this.aesStatusQueryRef.refetch();
    }
  }
}
